import { nanoid } from "nanoid";
import type { StoreApi } from "zustand";

import { fetchComponentGenomes, fetchSyntenyInfo } from "../api/fetch.ts";
import { setupSyntenyWorkerMessageHandler } from "../main-canvas/messages/handle-alignment-messages.ts";
import { refreshTracks } from "../main-canvas/messages/post-track-messages.ts";
import { refreshMain } from "../main-canvas/render-main.ts";
import SyntenyWorker from "../synteny-worker/index.ts?worker";
import type { Track, TrackGroup } from "../types.ts";
import { FetchError } from "../utils/errors.ts";
import type { TrackConfig } from "../utils/tracks/create-tracks.ts";
import { getSyntenyTypeBySequencingType, getTrackTypeBySequencingType } from "../utils/type-conversion.ts";
import { type GenomeViewerActions, type GenomeViewerStore } from "./store.ts";

// Zustand function
export function createTracksLoader(
  set: StoreApi<GenomeViewerStore>["setState"],
  get: StoreApi<GenomeViewerStore>["getState"],
): GenomeViewerActions["loadTracks"] {
  return async function (syntenyOrgId: number, initialContigs: number[], onError: (error: Error | FetchError) => void) {
    try {
      const syntenyInfo = await fetchSyntenyInfo(syntenyOrgId);
      const data = await fetchComponentGenomes(syntenyOrgId);

      const organismMap: GenomeViewerStore["organisms"] = new Map();
      const trackGroups = new Map<string, TrackGroup>();
      const storeUpdate: Partial<GenomeViewerStore> = {
        tracks: new Map<string, Track>(),
        trackGroups: new Map<string, TrackGroup>(),
        scrollX: 0,
        scrollY: 0,
        asynchronousScrolling: false,
        syntenyOrganism: { id: syntenyInfo.id, name: syntenyInfo.name, type: syntenyInfo.sequencingType },
        activeSyntenyType: getSyntenyTypeBySequencingType(syntenyInfo.sequencingType),
      };
      const trackConfigs: TrackConfig[] = [];

      // Process all organisms first
      for (const organism of data) {
        const { id, name, contigs } = organism;
        const trackIds: string[] = [];

        // Each organism is a new group since we can load an organism more than once
        const groupId = nanoid();

        // Process only the first contig
        const initialContig = contigs.filter((ctg) => initialContigs.includes(ctg.id));
        const contig = (initialContig.length > 0 ? initialContig : contigs)[0];

        if (!contig) {
          continue;
        }

        const contigsMap = new Map(contigs.map((contig) => [contig.id, contig]));
        const baseShift = 0;

        // Create and store track group data
        const groupData: TrackGroup = {
          groupId,
          contigId: contig.id,
          contigName: contig.name,
          organismName: organism.name,
          organismId: organism.id,
          trackIds,
          graphics: {
            baseShift,
            isVisible: true,
            length: contig.length,
          },
        };

        trackGroups.set(groupId, groupData);
        organismMap.set(id, { id, name, contigs: contigsMap });

        // Store the state updates
        storeUpdate.organisms = organismMap;
        storeUpdate.trackGroups = trackGroups;

        const initialTrackType = getTrackTypeBySequencingType(syntenyInfo.sequencingType);

        trackConfigs.push({ trackType: initialTrackType, group: groupId });
      }

      if (!get().syntenyWorker) {
        storeUpdate.syntenyWorker = new SyntenyWorker();
      }

      set(storeUpdate);
      get().createTracks(trackConfigs, onError);
      refreshTracks();
      refreshMain(); // because of new groups
      setupSyntenyWorkerMessageHandler(set, get);
    } catch (error) {
      if (error instanceof FetchError || error instanceof Error) {
        onError(error);
      }
    }
  };
}
